import { z } from 'zod';
import type { BlockDefinition } from '../types';

export const logosCloudSchema = z.object({
  eyebrow: z.string().max(120).optional(),
  title: z.string().max(200).optional(),
  variant: z.enum(['grid', 'bar', 'marquee']).default('grid'),
  grayscale: z.boolean().default(true).optional(),
  logos: z
    .array(
      z.object({
        src: z.string().min(1).max(500),
        alt: z.string().min(1).max(120),
        href: z.string().max(500).optional(),
      }),
    )
    .min(1)
    .max(24),
});

export type LogosCloudData = z.infer<typeof logosCloudSchema>;

export function LogosCloudRenderer({ eyebrow, title, variant = 'grid', grayscale = true, logos }: LogosCloudData) {
  const classes = `v5-logos v5-logos-${variant}${grayscale ? ' is-grayscale' : ''}`;
  return (
    <section className="v5-section v5-logos-section">
      <div className="container">
        {(eyebrow || title) && (
          <div className="v5-section-head">
            {eyebrow ? <div className="v5-eyebrow">{eyebrow}</div> : null}
            {title ? <h2 className="v5-h2-sm">{title}</h2> : null}
          </div>
        )}
        <div className={classes}>
          {logos.map((l, i) => {
            const img = <img src={l.src} alt={l.alt} loading="lazy" />;
            return (
              <div key={i} className="v5-logos-item">
                {l.href ? <a href={l.href} aria-label={l.alt}>{img}</a> : img}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export const logosCloudBlock: BlockDefinition<LogosCloudData> = {
  meta: {
    type: 'logos-cloud',
    label: 'Logos de clientes / social proof',
    description: 'Grid o barra horizontal de logos. Soporta blanco y negro automático.',
    category: 'social-proof',
    icon: 'briefcase',
  },
  schema: logosCloudSchema,
  defaults: {
    eyebrow: 'CONFÍAN EN NOSOTROS',
    title: 'Empresas que ya operan con nosotros',
    variant: 'grid',
    grayscale: true,
    logos: [
      { src: '/assets/logos/placeholder-1.svg', alt: 'Cliente 1' },
      { src: '/assets/logos/placeholder-2.svg', alt: 'Cliente 2' },
      { src: '/assets/logos/placeholder-3.svg', alt: 'Cliente 3' },
      { src: '/assets/logos/placeholder-4.svg', alt: 'Cliente 4' },
      { src: '/assets/logos/placeholder-5.svg', alt: 'Cliente 5' },
      { src: '/assets/logos/placeholder-6.svg', alt: 'Cliente 6' },
    ],
  },
  fields: [
    { name: 'eyebrow', label: 'Eyebrow', type: 'text', max: 120 },
    { name: 'title', label: 'Título (opcional)', type: 'text', max: 200 },
    {
      name: 'variant',
      label: 'Distribución',
      type: 'select',
      options: [
        { label: 'Grid 3-6 columnas', value: 'grid' },
        { label: 'Barra horizontal', value: 'bar' },
        { label: 'Marquee infinito', value: 'marquee' },
      ],
    },
    { name: 'grayscale', label: 'Blanco y negro automático', type: 'boolean' },
    {
      name: 'logos',
      label: 'Logos',
      type: 'array',
      max: 24,
      itemFields: [
        { name: 'src', label: 'URL del logo', type: 'image', required: true },
        { name: 'alt', label: 'Texto alternativo (alt)', type: 'text', required: true, max: 120 },
        { name: 'href', label: 'Enlace (opcional)', type: 'url' },
      ],
    },
  ],
  Renderer: LogosCloudRenderer,
};
